import { useRolodexEntries } from 'features/rolodex'
import _ from 'lodash'
import { useMemo } from 'react'
import { SWRResponse } from 'swr'
import { GeckoEntry, RolodexEntryType } from 'types'

import { CORE_SCHEMA } from './healthOntology'

const CUSTOM_SCHEMA_ADDITIONS_ENTRY_TYPE = RolodexEntryType.EXPLORER_DATA_SCHEMA

type SchemaFieldDef = {
  name: string
  type: DrilldownFieldType
  customColumns?: ReadonlyArray<TableSchemaColumnDef>

  // Used for enum fields to register allowed values
  allowedValues?: ReadonlyArray<string>
}

export type DrilldownFieldType =
  | 'text'
  | 'number'
  | 'enum'
  | 'boolean'
  | 'date'
  | 'object'
  | 'array'

export type DrilldownField = keyof (typeof CORE_SCHEMA)['fields'] | string
export type DrilldownRolodexType =
  | keyof (typeof CORE_SCHEMA)['typeNames']
  | string

export interface DrilldownSchemaConfig {
  typeToRequiredTags: Readonly<Record<string, ReadonlyArray<string>>>
  typeNames: Readonly<Record<string, string>>
  fields: Readonly<Record<string, SchemaFieldDef>>
}

export interface TableSchemaColumnDef {
  copyField?: DrilldownField
  columnTitle?: string
  tagColor?: string
  linkTemplate?: string
  name: string
}

function validateFieldEntry(key: string, value: SchemaFieldDef) {
  if (key.indexOf('.') < 0) {
    throw new Error(
      `Field key [${key}] had no '.' separator - fields should be defined as '(entry-type).(subpath-from-data)`
    )
  }

  if (_.isNil(value.name) || _.isNil(value.type)) {
    throw new Error(`Mailformed field def for key ${key}: ${value}`)
  }
}

function configFromEntry(entry: GeckoEntry): DrilldownSchemaConfig {
  const typeToRequiredTags = (entry.data['typeToRequiredTags'] ??
    {}) as DrilldownSchemaConfig['typeToRequiredTags']

  const fields = (entry.data['fields'] ?? {}) as DrilldownSchemaConfig['fields']

  Object.entries(fields).forEach(([key, value]) => {
    validateFieldEntry(key, value)
  })

  const inferredTypes = _.uniq(
    // Split + index access should be safe since we have run validation above
    Object.keys(fields).map((field) => field.split('.')[0])
  )
  const manualTypeNames = entry.data['typeNames'] ?? {}

  const inferredTypeNames = Object.fromEntries(
    inferredTypes.map((type) => [type, _.startCase(type)])
  )

  // Priorities manually-specified names
  const mergedTypeNames = _.merge([], inferredTypeNames, manualTypeNames)

  return {
    typeNames: mergedTypeNames,
    typeToRequiredTags,
    fields,
  }
}

export function deriveIdFields(
  types: ReadonlyArray<DrilldownRolodexType>,
  typeNames: Readonly<Record<string, string>>
): DrilldownSchemaConfig['fields'] {
  return Object.fromEntries(
    types.map((type) => {
      // Use the type name from schema if available, otherwise convert the key
      const label = typeNames[type] || convertKeyToLabel(type)
      return [`${type}.id`, { name: `${label} ID`, type: 'text' }]
    })
  )
}

export function convertKeyToLabel(key: string): string {
  return key
    .replace(/[-_]/g, ' ') // Replace '-' and '_' with spaces
    .split(' ') // Split the string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' ') // Join the words back into a single string
}

/**
 * Loads custom drilldown schemas.
 * Loads all org-level schemas as well as site-level schemas, if on a site.
 * @param orgId the org ID to get configs for
 * @param siteId the site ID to get configs for
 * @returns the SWR for the loaded configs
 */
export function useLoadCustomDrilldownSchemaAdditions(
  orgId: string | undefined,
  siteId: string | undefined
): SWRResponse<ReadonlyArray<DrilldownSchemaConfig>> {
  // There is an assumption that site level schemas are always tagged to the site's org,
  // so we only need to make one request of schemas for the current org to get both org and site schemas.
  const {
    entries: candidateEntries,
    isLoading,
    forceRefresh,
    isValidating,
    error,
  } = useRolodexEntries(
    useMemo(
      () =>
        orgId
          ? [
              {
                type: CUSTOM_SCHEMA_ADDITIONS_ENTRY_TYPE,
                tags: { organization: orgId },
              },
            ]
          : [],
      [orgId]
    )
  )

  // If any entries are tagged to a site,
  // don't keep them unless we are on that exact site.
  const relevantEntries = useMemo(
    () =>
      candidateEntries.filter((e) => {
        const isOrgLevelSchema = _.isNil(e.tags['site'])
        const isMatchingSiteLevelSchema = siteId && e.tags['site'] === siteId
        return isOrgLevelSchema || isMatchingSiteLevelSchema
      }),
    [candidateEntries, siteId]
  )

  return useMemo(
    () => ({
      data: relevantEntries.map(configFromEntry),
      isLoading: isLoading,
      mutate: async () => {
        await forceRefresh()
        return undefined
      },
      isValidating,
      error,
    }),
    [relevantEntries, error, forceRefresh, isLoading, isValidating]
  )
}

/**
 * Merge together multiple schemas, in order - later schemas will override
 * common fields of older ones.
 * @param configs the configs to merge
 * @returns the merged schema config
 */
export function mergeSchemas(
  ...configs: ReadonlyArray<DrilldownSchemaConfig>
): DrilldownSchemaConfig {
  return _.reduce(
    configs,
    (acc, curr) => {
      return {
        typeToRequiredTags: {
          ...acc.typeToRequiredTags,
          ...curr.typeToRequiredTags,
        },
        fields: {
          ...acc.fields,
          ...curr.fields,
        },
        typeNames: {
          ...acc.typeNames,
          ...curr.typeNames,
        },
      }
    },
    {
      typeNames: {},
      typeToRequiredTags: {},
      fields: {},
    }
  )
}

export const FIELD_TYPE_DISPLAY_LABELS: ReadonlyArray<{
  value: DrilldownFieldType
  label: string
}> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
]
